<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h6 class="mb-0 text-muted">
        <span v-if="scenarios.length" class="badge badge-secondary mr-1">{{ scenarios.length }}</span>
        cenário(s) cadastrado(s)
        <span v-if="pending > 0" class="badge badge-warning ml-1">{{ pending }} pendente(s)</span>
      </h6>
      <button class="btn btn-sm btn-primary" @click="openModal()">
        <i class="fas fa-plus mr-1"></i> Novo Cenário
      </button>
    </div>

    <div>
      <div v-if="loading" class="text-center py-4">
        <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
      </div>
      <div v-else-if="scenarios.length === 0" class="text-center py-4 text-muted">
        <i class="fas fa-vials fa-2x mb-2"></i>
        <p class="mb-0">Nenhum cenário de teste cadastrado.</p>
      </div>

      <div v-else class="sc-list">
        <div
          v-for="sc in scenarios"
          :key="sc.id"
          class="sc-block"
        >
          <!-- Cabeçalho do cenário -->
          <div class="sc-header" @click="toggleExpand(sc.id)">
            <div class="sc-header-left">
              <i :class="expanded[sc.id] ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="sc-chevron"></i>
              <span class="sc-title">{{ sc.title }}</span>
            </div>
            <div class="sc-header-right">
              <span :class="sc.status === 'concluido' ? 'badge badge-success' : 'badge badge-warning'">
                {{ sc.status === 'concluido' ? 'Concluído' : 'Pendente' }}
              </span>
              <button class="btn btn-sm btn-crud edit" title="Editar" @click.stop="openModal(sc)">
                <i class="fas fa-pencil-alt"></i>
              </button>
              <button class="btn btn-sm btn-crud delete" title="Excluir" @click.stop="destroy(sc)">
                <i class="far fa-trash-alt"></i>
              </button>
            </div>
          </div>

          <!-- Conteúdo expandido -->
          <div v-if="expanded[sc.id]" class="sc-body">
            <!-- Descrição do cenário -->
            <div class="sc-description ck-content" v-html="sc.description"></div>

            <!-- Arquivos do cenário -->
            <div v-if="sc.files && sc.files.length" class="sc-section">
              <div class="sc-section-title"><i class="fas fa-paperclip mr-1"></i>Arquivos do cenário</div>
              <div v-for="f in sc.files" :key="f.id" class="sc-file-link">
                <a :href="fileUrl(f)" target="_blank">
                  <i class="fas fa-file mr-1"></i>{{ f.original_name || f.file.split('/').pop() }}
                </a>
              </div>
            </div>

            <!-- Evidência (se concluído) -->
            <div v-if="sc.status === 'concluido' && sc.evidence" class="sc-evidence">
              <div class="sc-evidence-header">
                <span><i class="fas fa-check-circle mr-1 text-success"></i><strong>Evidência</strong></span>
                <small v-if="sc.evidenced_by">
                  por {{ sc.evidenced_by.name }} em {{ formatDate(sc.evidenced_at) }}
                </small>
              </div>
              <div class="ck-content" v-html="sc.evidence"></div>
            </div>

            <!-- Formulário de evidência (se pendente) -->
            <div v-if="sc.status === 'pendente'" class="sc-evidence-form">
              <div v-if="evidenceOpen[sc.id]">
                <label class="sc-evidence-label">Adicionar Evidência</label>
                <ckeditor v-model="evidenceText[sc.id]" :editor="editor" :config="editorConfig" />
                <div class="d-flex justify-content-end mt-2" style="gap: 8px">
                  <button class="btn btn-sm btn-secondary" @click="closeEvidence(sc.id)">Cancelar</button>
                  <button
                    class="btn btn-sm btn-success"
                    :disabled="savingEvidence[sc.id]"
                    @click="submitEvidence(sc)"
                  >
                    <i v-if="savingEvidence[sc.id]" class="fas fa-spinner fa-spin mr-1"></i>
                    Salvar Evidência
                  </button>
                </div>
              </div>
              <button v-else class="btn btn-sm btn-outline-success" @click="openEvidence(sc.id)">
                <i class="fas fa-pen mr-1"></i> Adicionar Evidência
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Novo / Editar Cenário -->
    <div v-if="showModal" class="modal-backdrop-custom" @click.self="closeModal">
      <div class="modal-dialog-custom modal-lg-custom">
        <div class="card mb-0">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">{{ form.id ? 'Editar Cenário de Teste' : 'Novo Cenário de Teste' }}</h5>
            <button type="button" class="btn btn-sm btn-light" @click="closeModal">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label>Título <span class="required">*</span></label>
              <input v-model="form.title" type="text" class="form-control" placeholder="Título do cenário" />
            </div>
            <div class="form-group">
              <label>Descrição <span class="required">*</span></label>
              <ckeditor v-model="form.description" :editor="editor" :config="editorConfig" />
            </div>
            <div class="form-group">
              <label>Arquivos de apoio</label>
              <input type="file" class="form-control" multiple @change="onFileChange" />
              <ul v-if="form.newFiles.length" class="mt-1 pl-3 mb-0">
                <li v-for="(f, i) in form.newFiles" :key="i" class="small">
                  {{ f.name }} ({{ (f.size / 1024).toFixed(1) }} KB)
                  <button type="button" class="btn btn-link p-0 ml-1" style="font-size: 0.8rem" @click="form.newFiles.splice(i, 1)">remover</button>
                </li>
              </ul>
            </div>
          </div>
          <div class="card-footer d-flex justify-content-end">
            <button class="btn btn-secondary mr-2" @click="closeModal">Cancelar</button>
            <button class="btn btn-primary" :disabled="saving" @click="save">
              <i v-if="saving" class="fas fa-spinner fa-spin mr-1"></i>
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

class MyUploadAdapter {
  constructor(loader) { this.loader = loader; }
  upload() {
    if (!this.loader || !this.loader.file) return Promise.reject('No file to upload');
    return this.loader.file.then(file => {
      if (!file) return Promise.reject('Invalid file');
      return new Promise((resolve, reject) => {
        this._initRequest();
        this._initListeners(resolve, reject, file);
        this._sendRequest(file);
      });
    });
  }
  abort() { if (this.xhr) this.xhr.abort(); }
  _initRequest() {
    const xhr = (this.xhr = new XMLHttpRequest());
    xhr.open('POST', 'https://api.intranet.it4d.com.br/api/v1/images/upload', true);
    xhr.responseType = 'json';
  }
  _initListeners(resolve, reject, file) {
    const xhr = this.xhr;
    const loader = this.loader;
    xhr.addEventListener('error', () => reject(`Couldn't upload file: ${file.name}.`));
    xhr.addEventListener('abort', () => reject());
    xhr.addEventListener('load', () => {
      const response = xhr.response;
      if (!response || response.error) return reject(response?.error?.message || 'Upload error');
      resolve({ default: response.url });
    });
    if (xhr.upload && loader) {
      xhr.upload.addEventListener('progress', evt => {
        if (evt.lengthComputable) { loader.uploadTotal = evt.total; loader.uploaded = evt.loaded; }
      });
    }
  }
  _sendRequest(file) {
    const data = new FormData();
    data.append('upload', file);
    this.xhr.send(data);
  }
}

function MyUploadAdapterPlugin(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = loader => new MyUploadAdapter(loader);
}

export default {
  name: 'CallTestScenarios',

  props: {
    callId: { type: [Number, String], required: true },
  },

  data() {
    return {
      scenarios: [],
      loading: true,
      showModal: false,
      saving: false,
      expanded: {},
      evidenceOpen: {},
      evidenceText: {},
      savingEvidence: {},
      form: this.emptyForm(),
      editor: ClassicEditor,
      editorConfig: { extraPlugins: [MyUploadAdapterPlugin] },
    };
  },

  computed: {
    pending() {
      return this.scenarios.filter(s => s.status === 'pendente').length;
    },
  },

  mounted() {
    this.fetchScenarios();
  },

  methods: {
    emptyForm() {
      return { id: null, title: '', description: '', newFiles: [] };
    },

    fetchScenarios() {
      this.loading = true;
      axios
        .get(this.$store.state.api + `calls/${this.callId}/test-scenarios`)
        .then(res => { this.scenarios = res.data.data || res.data; })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    toggleExpand(id) {
      this.$set(this.expanded, id, !this.expanded[id]);
    },

    openModal(sc = null) {
      this.form = sc
        ? { id: sc.id, title: sc.title, description: sc.description, newFiles: [] }
        : this.emptyForm();
      this.showModal = true;
    },

    closeModal() { this.showModal = false; },

    onFileChange(e) {
      Array.from(e.target.files).forEach(f => this.form.newFiles.push(f));
      e.target.value = '';
    },

    save() {
      if (!this.form.title.trim() || !this.form.description.trim()) {
        this.$message('Atenção', 'Título e descrição são obrigatórios.', 'warning');
        return;
      }
      this.saving = true;
      const fd = new FormData();
      fd.append('title', this.form.title);
      fd.append('description', this.form.description);
      this.form.newFiles.forEach(f => fd.append('files[]', f));

      const req = this.form.id
        ? axios.post(this.$store.state.api + `test-scenarios/${this.form.id}?_method=PUT`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        : axios.post(this.$store.state.api + `calls/${this.callId}/test-scenarios`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });

      req
        .then(() => {
          this.$message('Sucesso', 'Cenário salvo!', 'success');
          this.closeModal();
          this.fetchScenarios();
        })
        .catch(() => { this.$message('Erro', 'Não foi possível salvar.', 'error'); })
        .finally(() => { this.saving = false; });
    },

    openEvidence(id) {
      this.$set(this.evidenceOpen, id, true);
      if (!this.evidenceText[id]) this.$set(this.evidenceText, id, '');
    },

    closeEvidence(id) {
      this.$set(this.evidenceOpen, id, false);
    },

    submitEvidence(sc) {
      const text = this.evidenceText[sc.id];
      if (!text || !text.trim()) {
        this.$message('Atenção', 'A evidência não pode estar vazia.', 'warning');
        return;
      }
      this.$set(this.savingEvidence, sc.id, true);
      axios
        .post(this.$store.state.api + `test-scenarios/${sc.id}/evidence`, { evidence: text })
        .then(res => {
          const idx = this.scenarios.findIndex(s => s.id === sc.id);
          if (idx !== -1) this.$set(this.scenarios, idx, res.data);
          this.closeEvidence(sc.id);
          this.$set(this.evidenceText, sc.id, '');
          this.$message('Sucesso', 'Evidência adicionada!', 'success');
        })
        .catch(() => { this.$message('Erro', 'Não foi possível salvar a evidência.', 'error'); })
        .finally(() => { this.$set(this.savingEvidence, sc.id, false); });
    },

    destroy(sc) {
      swal({
        title: 'Deseja excluir este cenário?',
        type: 'warning',
        buttons: {
          cancel: { text: 'Não', value: 'cancel', className: 'btn-danger' },
          confirm: { text: 'Sim', value: 'confirm' },
        },
      }).then(v => {
        if (v === 'confirm') {
          axios
            .delete(this.$store.state.api + `test-scenarios/${sc.id}`)
            .then(() => { this.fetchScenarios(); })
            .catch(() => { this.$message('Erro', 'Não foi possível excluir.', 'error'); });
        }
      });
    },

    fileUrl(f) {
      return this.$store.state.apiBase + `test-scenario-files/${f.id}/download`;
    },

    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleString('pt-BR');
    },
  },
};
</script>

<style scoped>
/* ── lista de cenários ──────────────────────────────── */
.sc-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sc-block {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  color: #343a40 !important;
}

.sc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f8f9fa;
  cursor: pointer;
  gap: 8px;
}
.sc-header:hover { background: #eef0f3; }

.sc-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}
.sc-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.sc-chevron { color: #6c757d; font-size: 0.8rem; }
.sc-title { font-weight: 600; color: #212529; }

/* ── conteúdo expandido ─────────────────────────────── */
.sc-body {
  padding: 14px 16px;
  background: #fff;
  border-top: 1px solid #dee2e6;
  color: #343a40 !important;
}

.sc-description {
  margin-bottom: 12px;
  color: #343a40;
  line-height: 1.6;
}

/* ── seção de arquivos ──────────────────────────────── */
.sc-section {
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #6c757d;
}
.sc-section-title {
  font-weight: 600;
  color: #495057;
  margin-bottom: 6px;
  font-size: 0.85rem;
}
.sc-file-link a { color: #007bff; font-size: 0.85rem; display: inline-block; margin-bottom: 2px; }

/* ── evidência (concluído) ──────────────────────────── */
.sc-evidence {
  padding: 12px 14px;
  background: #eafaf0;
  border-radius: 4px;
  border-left: 3px solid #28a745;
  margin-bottom: 10px;
  color: #155724;
}
.sc-evidence-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 0.85rem;
  color: #155724;
}
.sc-evidence-header small { color: #6c757d; }

/* ── formulário de evidência (pendente) ─────────────── */
.sc-evidence-form {
  padding: 12px 14px;
  background: #fff8e1;
  border-radius: 4px;
  border-left: 3px solid #ffc107;
}
.sc-evidence-label {
  display: block;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 6px;
}

/* ── sub-modal (Novo / Editar cenário) ──────────────── */
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1070;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.modal-lg-custom {
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow-y: auto;
}
.modal-lg-custom .card {
  background: #fff !important;
  color: #343a40 !important;
}
.modal-lg-custom .card-header,
.modal-lg-custom .card-body,
.modal-lg-custom .card-footer {
  background: #fff !important;
  color: #343a40 !important;
}
.modal-lg-custom label,
.modal-lg-custom input,
.modal-lg-custom h5 {
  color: #343a40 !important;
}
</style>
